import FAQSection from "@/components/common/faqSection";

const faqLists = [
  {
    id: "platform-overview",
    title: "Platform Overview",
    items: [
      {
        id: "what-is-sml-connect",
        question: "What is SML Connect?",
        answer:
          "SML Connect is a connected health and safety management platform that brings audits, risk assessments, incidents, actions and compliance activity together in one place. It is designed to help managers, consultants and site teams stay on top of health and safety responsibilities through structured workflows, dashboards and reporting.",
      },
      {
        id: "who-is-it-for",
        question: "Who is SML Connect designed for?",
        answer:
          "SML Connect is built for organisations across manufacturing, logistics, construction, engineering and retail, as well as the consultants and health and safety professionals who support them. It is suitable for single-site teams and multi-site organisations with complex operational structures.",
      },
      {
        id: "multi-site",
        question: "Can SML Connect handle multi-site organisations?",
        answer:
          "Yes. The platform supports multi-site account structures with departments, role-based permissions and user management, making it straightforward to manage access and visibility across different locations and teams.",
      },
      {
        id: "platform-customisation",
        question:
          "Can the platform be customised to match our existing processes?",
        answer:
          "Yes. SML Connect is designed to be configured around the way your organisation already works. During onboarding, templates, workflows, user permissions and dashboard layouts are set up to reflect your existing processes rather than requiring you to adapt to a rigid system.",
      },
      {
        id: "platform-scaling",
        question: "Does the platform scale as our organisation grows?",
        answer:
          "Yes. SML Connect is built to support organisations from single-site operations through to large multi-site enterprises. As your organisation grows, you can add additional sites, users and modules without needing to migrate to a different system or rebuild your existing setup.",
      },
      {
        id: "platform-support",
        question: "What support is available if we run into issues?",
        answer:
          "SML provides ongoing support to all platform users, including access to the SML team for technical queries, guidance on best practice and help with configuring workflows or reports. Support is available by phone and email during business hours.",
      },
    ],
  },
  {
    id: "using-platform",
    title: "Using the Platform",
    items: [
      {
        id: "mobile-access",
        question: "Can the platform be used on mobile or tablet?",
        answer:
          "Yes. SML Connect is responsive across desktop, tablet and mobile devices, with PWA support for iOS and Android. Teams can complete checks, upload evidence and log observations from the field without needing to be at a desk.",
      },
      {
        id: "onboarding",
        question: "How long does it take to get set up and start using it?",
        answer:
          "Setup times vary depending on the size and complexity of your organisation. SML's team works with you during onboarding to configure templates, user permissions and workflows, helping the platform reflect your existing processes rather than requiring you to change how you work.",
      },
      {
        id: "training",
        question: "Is training provided for our team?",
        answer:
          "Yes. SML provides consultant-led training and ongoing support to help your team get the most out of the platform. This includes guidance on building workflows, running audits and using the reporting tools effectively.",
      },
      {
        id: "user-roles",
        question: "How are user roles and permissions managed?",
        answer:
          "User roles and permissions are managed through the platform administration area, where you can define role-based access levels, assign users to specific sites or departments and control what each user can view, create or edit. This makes it straightforward to maintain appropriate access across your organisation.",
      },
      {
        id: "reporting",
        question: "Can I create custom reports and dashboards?",
        answer:
          "Yes. SML Connect includes configurable dashboards and reporting tools that allow you to track the metrics that matter most to your organisation. You can monitor KPI performance, compliance trends, outstanding actions and incident data, with the ability to filter and compare across sites and time periods.",
      },
    ],
  },
  {
    id: "data-and-security",
    title: "Data & Security",
    items: [
      {
        id: "data-control",
        question: "Who controls our data on the platform?",
        answer:
          "You retain full control over your data. SML Connect uses role-based permissions and structured access controls so the right people have access to the right information. Data is stored securely and access can be managed at user, site and organisational level.",
      },
      {
        id: "data-ownership",
        question: "Can we export our data if we decide to leave?",
        answer:
          "Yes. SML Connect provides export capabilities so you can maintain access to your records, audit history and compliance data should you ever need to move to another system.",
      },
      {
        id: "security",
        question: "How is data kept secure?",
        answer:
          "The platform uses role-based access controls, secure authentication and activity logging to maintain data integrity and accountability. Infrastructure is hosted in UK data centres with appropriate security certifications.",
      },
      {
        id: "data-retention",
        question: "How long is data retained on the platform?",
        answer:
          "Data retention periods are configurable based on your organisational requirements and any relevant legal or regulatory obligations. SML works with you during onboarding to establish appropriate retention schedules for different record types.",
      },
      {
        id: "data-backups",
        question: "Are regular backups taken of our data?",
        answer:
          "Yes. The platform infrastructure includes automated backup procedures to protect against data loss. Backup frequency and retention are managed in line with industry best practice and can be discussed in more detail during the onboarding process.",
      },
      {
        id: "data-compliance",
        question: "Is the platform GDPR compliant?",
        answer:
          "Yes. SML Connect is designed to support GDPR compliance through structured data management, access controls, consent tracking and data export capabilities. SML acts as a data processor and works with organisations to help them meet their data protection obligations as data controllers.",
      },
    ],
  },
];

export default function PlatformFAQSection() {
  return (
    <FAQSection
      title="Platform FAQs"
      description="Common questions about how SML Connect works, who it is for and how your data is managed."
      lists={faqLists}
      defaultOpenIndex={0}
    />
  );
}
